import { useState, useCallback } from "react";
import { request } from "../utils/api"; // Import the API utility

/**
 * Custom hook for making API requests with state management
 * 
 * @returns {Object} API hook object containing state and methods
 * @returns {any} data - The response data from the most recent API call
 * @returns {boolean} loading - Whether an API request is currently in progress
 * @returns {string|null} error - Error message if the request failed
 * @returns {Object|null} errorObj - Detailed error object if the request failed
 * @returns {Function} callApi - Function to make API requests
 */
export const useApi = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorObj, setErrorObj] = useState(null);

  /**
   * Makes an API request with the specified parameters
   * 
   * @param {string} endpoint - The API endpoint to call
   * @param {string} [method="GET"] - The HTTP method to use
   * @param {Object|null} [body=null] - The request body for POST/PUT requests
   * @param {Object|null} [params=null] - URL query parameters
   * @param {Object} [headers={}] - Additional request headers
   * @param {string} [responseType='json'] - Expected response type
   * @returns {Promise<Object>} Response object with success flag and data or error
   */
  const callApi = useCallback(
    async (endpoint, method = "GET", body = null, params = null, headers = {}, responseType = 'json') => {
      console.log("API call triggered:", { endpoint, method, body, params }); // Debugging log
      setLoading(true);
      setError(null);
      setErrorObj(null);
      try {
        const response = await request({ endpoint, method, body, params, headers, responseType });
        console.log("API response received:", response); // Debugging log
        setData(response);
        return response; // Return success and data
      } catch (err) {
        console.error("API request error:", err); // Error log
        setError(err?.error?.message || "An unexpected error occurred.");
        setErrorObj(err?.error || "An unexpected error occurred.");
        return { success: false, error: err?.error?.message, errorObj: err?.error }; // Return error
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { data, loading, error, errorObj, callApi };
};

export default useApi;